/**
 * Which dynamic attribute carries which meaning, and which library class maps
 * to which material kind.
 *
 * Both are DATA, not code. The library evolves without this module — a new
 * class, a renamed marker — and 4 of the technical spec is explicit that adding
 * one must never require a deploy. So every name lives in a document that ships
 * as a factory preset, is overridable per user and per project, and is what the
 * diagnostic screen edits.
 *
 * The factory values were read off the tenant's own modules rather than
 * invented: `calcmepeca`, `calcmemat`, `calcmedesc`, `cafita`, `fs`/`fsi`/
 * `fsld`/`fsle` and the rest are the names those files actually carry.
 */

import type { MaterialKind } from "../model/material";

/** Every meaning the extraction knows how to look for. */
export type AttributeRole =
  // 2.1 — structural classification
  | "partMarker"
  | "containerLevel"
  | "containerLabel"
  | "innerContainer"
  | "frontMarker"
  | "hiddenMarker"
  | "auxiliaryMarker"
  | "editableMarker"
  | "replaceableFront"
  | "itemId"
  // 2.2 — material classification
  | "materialClass"
  | "faceKind"
  | "edgeBandWidth"
  | "commercialName"
  | "materialName"
  // 2.3 — declared dimensions
  | "declaredLength"
  | "declaredWidth"
  | "declaredThickness"
  | "declaredEdgeLength"
  | "declaredMachiningLength"
  // 2.4 — quantities
  | "cutQuantity"
  | "declaredQuantity"
  | "groupingFactor"
  // 2.5 — identification
  | "partDescription"
  | "componentName"
  | "nativeDescription"
  | "descriptionSuffix"
  | "sizeCode"
  // 2.6 — process
  | "edgeLength"
  | "edgeType"
  | "machiningLength"
  | "machiningType"
  | "fasteningType"
  | "fasteningQuantity"
  | "weldType"
  | "weldQuantity"
  | "mitreType"
  | "mitreQuantity"
  | "accessoryProfileType"
  | "accessoryProfileQuantity"
  // grain and schematic geometry
  | "grainAxis"
  | "schematicGeometry";

/**
 * The attribute names a role may be written under, in priority order. A list
 * rather than a single name because a library in transition carries both the
 * old spelling and the new one, and refusing to read the old one would blank
 * out every module that has not been re-saved.
 */
export type AttributeDictionary = Record<AttributeRole, string[]>;

/** Roles inherited from the nearest ancestor that declares them (1.2). */
export const HERITABLE_ROLES: ReadonlySet<AttributeRole> = new Set<AttributeRole>([
  "frontMarker",
  "hiddenMarker",
  "itemId",
  "materialClass",
  "faceKind",
  "commercialName",
  "materialName",
  "grainAxis",
]);

export const FACTORY_DICTIONARY: AttributeDictionary = {
  partMarker: ["calcmepeca"],
  containerLevel: ["calcmecon"],
  containerLabel: ["calcmeconname"],
  innerContainer: ["calcmeint", "calcmeinterno"],
  frontMarker: ["calcmefront"],
  hiddenMarker: ["calcmehidden"],
  auxiliaryMarker: ["calcmeig"],
  editableMarker: ["calcmeedit"],
  replaceableFront: ["calcmetro"],
  itemId: ["calcmeid"],

  materialClass: ["calcmemat"],
  faceKind: ["calcmeface", "tipo_de_face"],
  edgeBandWidth: ["cafita"],
  commercialName: ["calcmename"],
  materialName: ["material"],

  declaredLength: ["comp"],
  declaredWidth: ["larg"],
  declaredThickness: ["esp"],
  declaredEdgeLength: ["compfita"],
  declaredMachiningLength: ["compusinagem"],

  cutQuantity: ["calcmecorte"],
  declaredQuantity: ["calcmeqtd", "qtc"],
  groupingFactor: ["calcmeconj"],

  partDescription: ["calcmedesc"],
  componentName: ["calcmename"],
  nativeDescription: ["description"],
  descriptionSuffix: ["int_desc"],
  sizeCode: ["calcmetam"],

  edgeLength: ["cafita"],
  edgeType: ["calcmefita", "tipofita"],
  machiningLength: ["calcmeusi"],
  machiningType: ["calcmeusitipo"],
  fasteningType: ["calcmepartipo"],
  fasteningQuantity: ["calcmepar"],
  weldType: ["calcmesoldatipo"],
  weldQuantity: ["calcmesolda"],
  mitreType: ["calcmeesqtipo"],
  mitreQuantity: ["calcmeesq"],
  accessoryProfileType: ["calcmeperftipo"],
  accessoryProfileQuantity: ["calcmeperf"],

  grainAxis: ["veio"],
  schematicGeometry: ["calcmeesquematico"],
};

/**
 * Which attribute holds the tape applied to each of the four edges.
 *
 * The tenant's modules write a thickness per side under `fs` / `fsi` / `fsld` /
 * `fsle` — front, back, right, left — with zero meaning no tape. That is a
 * different shape from the one-attribute-per-role dictionary above, so it gets
 * its own entry.
 */
export interface EdgeAttributeMap {
  ymin: string[];
  ymax: string[];
  xmin: string[];
  xmax: string[];
}

export const FACTORY_EDGE_ATTRIBUTES: EdgeAttributeMap = {
  ymin: ["fs"],
  ymax: ["fsi", "fep"],
  xmin: ["fsle"],
  xmax: ["fsld"],
};

export interface FaceAttributeMap {
  zmax: string[];
  zmin: string[];
}

export const FACTORY_FACE_ATTRIBUTES: FaceAttributeMap = {
  zmax: ["lamsup", "calcmelamsup"],
  zmin: ["laminf", "calcmelaminf"],
};

/* -------------------------------------------------------------------------- */
/* Class map                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Library class → cut-list material kind, per the table in 2.2 of the
 * dynamic-component spec. Keys are compared case-insensitively and without
 * accents, so "Composto de Alumínio" and "COMPOSITO DE ALUMINIO" agree.
 */
export type MaterialClassMap = Record<string, MaterialKind>;

export const FACTORY_CLASS_MAP: MaterialClassMap = {
  mdf: "panel",
  mdp: "panel",
  compensado: "panel",
  "painel de madeira": "panel",
  painel: "panel",
  fita: "edge_band",
  "fita de borda": "edge_band",
  metal: "bar",
  perfil: "bar",
  aluminio: "bar",
  "composito de aluminio": "panel",
  acm: "panel",
  vidro: "panel",
  espelho: "panel",
  porta: "hardware",
  ferragem: "hardware",
  acessorio: "hardware",
  "madeira macica": "solid_wood",
  macico: "solid_wood",
  laminado: "veneer",
  lamina: "veneer",
};

/** Classes whose parts must never rotate in a cut plan, whatever the material. */
export const FACTORY_GRAIN_LOCKED_CLASSES: readonly string[] = ["porta", "frente"];

/** Classes drawn schematically, whose declared size beats the measured one (3.3). */
export const FACTORY_SCHEMATIC_CLASSES: readonly string[] = ["fita", "fita de borda", "porta"];

export function normalizeClassKey(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toLocaleLowerCase("pt-BR");
}

/* -------------------------------------------------------------------------- */
/* The extraction schema                                                       */
/* -------------------------------------------------------------------------- */

/** Everything the extraction reads that is configuration rather than code. */
export interface ExtractionSchema {
  dictionary: AttributeDictionary;
  edgeAttributes: EdgeAttributeMap;
  faceAttributes: FaceAttributeMap;
  classMap: MaterialClassMap;
  grainLockedClasses: string[];
  schematicClasses: string[];
  /**
   * The value `partMarker` must hold for the entity to count as a part. Empty
   * means any non-empty value counts, which is what the tenant's files need:
   * they write the literal "PECAS".
   */
  partMarkerValue: string;
  frontMarkerValue: string;
  /** Divergence above this, between declared and measured, raises a flag (2.3). */
  dimensionToleranceMm: number;
  /** Material applied to the unseen face of a one-sided part (2.2). */
  defaultBackFaceMaterialId: string;
}

export const FACTORY_SCHEMA: ExtractionSchema = {
  dictionary: FACTORY_DICTIONARY,
  edgeAttributes: FACTORY_EDGE_ATTRIBUTES,
  faceAttributes: FACTORY_FACE_ATTRIBUTES,
  classMap: FACTORY_CLASS_MAP,
  grainLockedClasses: [...FACTORY_GRAIN_LOCKED_CLASSES],
  schematicClasses: [...FACTORY_SCHEMATIC_CLASSES],
  partMarkerValue: "",
  frontMarkerValue: "",
  dimensionToleranceMm: 1,
  defaultBackFaceMaterialId: "",
};

/**
 * Folds a user's overrides onto the factory schema. Names are appended rather
 * than replaced so an override adds a spelling without losing the ones that
 * already work; a role is only replaced outright when the override says so by
 * listing `replace`.
 */
export function mergeSchema(
  base: ExtractionSchema,
  override: Partial<ExtractionSchema> & { replaceDictionary?: boolean },
): ExtractionSchema {
  const dictionary = { ...base.dictionary };
  for (const [role, names] of Object.entries(override.dictionary ?? {})) {
    const key = role as AttributeRole;
    if (!names?.length) continue;
    dictionary[key] = override.replaceDictionary
      ? [...names]
      : [...new Set([...names, ...(base.dictionary[key] ?? [])])];
  }
  return {
    dictionary,
    edgeAttributes: { ...base.edgeAttributes, ...(override.edgeAttributes ?? {}) },
    faceAttributes: { ...base.faceAttributes, ...(override.faceAttributes ?? {}) },
    classMap: { ...base.classMap, ...(override.classMap ?? {}) },
    grainLockedClasses: override.grainLockedClasses ?? [...base.grainLockedClasses],
    schematicClasses: override.schematicClasses ?? [...base.schematicClasses],
    partMarkerValue: override.partMarkerValue ?? base.partMarkerValue,
    frontMarkerValue: override.frontMarkerValue ?? base.frontMarkerValue,
    dimensionToleranceMm: override.dimensionToleranceMm ?? base.dimensionToleranceMm,
    defaultBackFaceMaterialId: override.defaultBackFaceMaterialId ?? base.defaultBackFaceMaterialId,
  };
}

export function resolveClass(schema: ExtractionSchema, declared: string | undefined): {
  kind: MaterialKind | null;
  key: string;
} {
  if (!declared) return { kind: null, key: "" };
  const key = normalizeClassKey(declared);
  return { kind: schema.classMap[key] ?? null, key };
}
